import { FormEvent, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, Card, CircularProgress, TextField, Typography, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import petService from "@fuse/services/petService";
import jwtService from "../../auth/services/jwtService";
import RemovePetDialog from "../addpet/components/UpdatePetProfile/RemovePetDialog";

const fields = [
    ["firstName", "First name"], ["lastName", "Last name"], ["email", "Email"], ["phoneNumber", "Phone"],
    ["address1", "Address"], ["address2", "Address line 2"], ["city", "City"], ["zipcode", "ZIP code"]
] as const;
const api = process.env.VITE_APP_API_BASE_URL;
export default function CustomerAccountPage() {
    const [profile, setProfile] = useState<any>(null); const [pets, setPets] = useState<any[]>([]);
    const [states, setStates] = useState<any[]>([]); const [removing, setRemoving] = useState<any>(null);
    const [error, setError] = useState(""); const [notice, setNotice] = useState(""); const [busy, setBusy] = useState(false);
    const loadPets = async () => setPets((await axios.get(`${api}AccountAccess/pets`)).data.result || []);
    useEffect(() => {
        Promise.all([axios.get(`${api}AccountAccess/me`), axios.get(`${api}Utilities/states`), loadPets()])
            .then(([me, lookup]) => { setProfile(me.data.result); setStates(lookup.data.result || []); })
            .catch(() => setError("Unable to open your account. Reopen your email link or sign in normally."));
    }, []);
    async function save(event: FormEvent) {
        event.preventDefault(); setBusy(true); setError(""); setNotice("");
        try {
            const input = Object.fromEntries(fields.map(([key]) => [key, profile[key] || ""]));
            const response = await axios.put(`${api}AccountAccess/contact`, { ...input, stateId: profile.stateId || null });
            if (!response.data.succeeded) throw new Error();
            if (response.data.result.requiresNewLink) { jwtService.logout(); window.location.replace("/sign-in"); return; }
            setNotice("Your contact details were saved.");
        } catch { setError("Unable to save your details. Check your email and phone number, or contact Peeva support."); }
        finally { setBusy(false); }
    }
    async function updateCard() {
        setBusy(true); setError("");
        try {
            const response = await axios.post(`${api}AccountAccess/card`); const url = new URL(response.data.result?.url);
            if (!response.data.succeeded || url.protocol !== "https:" || url.hostname !== "billing.stripe.com") throw new Error();
            window.location.assign(url.href);
        } catch { setError("Unable to open secure card update. Please retry or contact Peeva support."); setBusy(false); }
    }
    return <main className="max-w-4xl mx-auto p-20 flex flex-col gap-20">
        <Card className="p-24"><Typography variant="h4">Your Peeva account</Typography>
            <Typography>Review your contact details, manage your pets, or update your payment card securely.</Typography>
            <Button onClick={() => { jwtService.logout(); window.location.replace("/sign-in"); }}>Sign out</Button>
            {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
        </Card>
        {!profile ? !error && <CircularProgress aria-label="Loading account" /> : <>
            <Card className="p-24"><Typography variant="h5">Contact details</Typography>
                <form onSubmit={save} className="flex flex-col gap-16 mt-16">
                    {fields.map(([key, label], index) => <TextField key={key} label={label} value={profile[key] || ""}
                        required={index < 4} type={key === "email" ? "email" : key === "phoneNumber" ? "tel" : "text"}
                        disabled={busy} onChange={event => setProfile({ ...profile, [key]: event.target.value })} />)}
                    <TextField select label="State" value={profile.stateId || ""} disabled={busy}
                        onChange={event => setProfile({ ...profile, stateId: event.target.value ? Number(event.target.value) : null })}>
                        <MenuItem value="">Select state</MenuItem>{states.map(state => <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>)}
                    </TextField>
                    <Typography variant="body2">Changing your email ends this secure session. You’ll need to sign in or request a new link afterward.</Typography>
                    <Button type="submit" variant="contained" disabled={busy}>Save contact details</Button>
                </form>
            </Card>
            <Card className="p-24"><Typography variant="h5">Your pets</Typography>
                {pets.map(pet => <div key={pet.id} className="flex items-center justify-between py-12">
                    <Typography>{pet.name}</Typography><Button onClick={() => setRemoving(pet)}>Remove</Button>
                </div>)}
                {!pets.length && <Typography>No pets are currently listed.</Typography>}
                <Button component={Link} to="/client/pet/add" variant="outlined">Add a pet</Button>
            </Card>
            <Card className="p-24"><Typography variant="h5">Payment card</Typography>
                <Typography>Your card details are managed securely by Stripe.</Typography>
                <Button onClick={updateCard} disabled={busy || !profile.hasBillingAccount}>Update card</Button>
                {!profile.hasBillingAccount && <Typography>No billing account is available. Please contact support if you need help.</Typography>}
            </Card>
        </>}
        {removing && <RemovePetDialog petId={removing.id} petName={removing.name} onClose={() => setRemoving(null)}
            onRemove={async (id, reason) => await petService.removeOwnedPet(id, reason)}
            onRemoved={() => { setRemoving(null); loadPets().catch(() => setError("Refresh this page to see the updated pet list.")); }} />}
    </main>;
}
